import React, { useState } from 'react';
import { Button, Input, Card, CardBody } from '@heroui/react';
import { CustomHeroButton, Typography } from '../../../../../components';
import { InfoIcon } from '../../../../../components/icons';
import IcUsr from './icon-usr.svg';
import clientAccService from '../../../../../api/client/clientAccService';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import AlertBlock from '../../../../../components/alert-block';
import CredentialsSuccessModal from './CredentialsSuccessModal';
import { handleApiError } from '../../../../../utils/errorHandler';
import { useTranslations } from '../../../../../contexts/translation.context';

interface CredentialsTabProps {
  accountData: {
    email: string;
    password: string;
    lastUpdated: string;
    id?: string;
  };
  onCredentialsUpdated?: () => void;
}

const getValidationSchema = (t: any) =>
  Yup.object({
    email: Yup.string()
      .email(t('accountPage.credentials.validation.emailInvalid'))
      .required(t('accountPage.credentials.validation.emailRequired')),
    password: Yup.string()
      .min(6, t('accountPage.credentials.validation.passwordMin'))
      .max(25, t('accountPage.credentials.validation.passwordMax'))
      .required(t('accountPage.credentials.validation.passwordRequired')),
  });

const CredentialsTab: React.FC<CredentialsTabProps> = ({ accountData, onCredentialsUpdated }) => {
  const { t } = useTranslations();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const handleSubmit = async (values: { email: string; password: string }) => {
    if (!accountData.id) {
      console.error('Model ID is required');
      return;
    }

    setError(null); // Очищаем предыдущую ошибку
    setIsUpdating(true);
    try {
      await clientAccService.setCredentials(accountData.id, values);
      // Успешное обновление - обновляем данные аккаунта
      if (onCredentialsUpdated) {
        onCredentialsUpdated();
      }
      // Показываем модальное окно об успехе
      setIsSuccessModalOpen(true);
    } catch (error: any) {
      console.error('Ошибка при обновлении учетных данных:', error);
      setError(handleApiError(error));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Форма доступа */}
      <div className="w-full md:max-w-[33.125rem]">
        <Typography variant="h6" className="mb-4">
          {t('accountPage.credentials.title')}
        </Typography>

        <Formik
          initialValues={{
            email: accountData.email,
            password: accountData.password,
          }}
          validationSchema={getValidationSchema(t)}
          onSubmit={(values, actions) => {
            handleSubmit(values);
            actions.setSubmitting(false);
          }}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            isSubmitting,
          }) => (
            <Form>
              <Card className="border border-gray-90 shadow-sm rounded-3.5">
                <CardBody className="p-4">
                  <div className="flex flex-col gap-4">
                    <div>
                      <Typography variant="body2" className="text-auth-ink mb-2">
                        {t('accountPage.credentials.labels.email')}
                      </Typography>
                      <Input
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="bordered"
                        className="w-full h-[2.375rem]"
                        classNames={{
                          inputWrapper: `border-auth-strength-none max-h-[2.375rem] min-h-[2.375rem] rounded-lg h-10 ${
                            errors.email && touched.email ? 'border-red-500' : ''
                          }`,
                        }}
                      />
                      {errors.email && touched.email && (
                        <div className="text-red-500 text-xs mt-1">{errors.email}</div>
                      )}
                    </div>
                    <div>
                      <Typography variant="body2" className="text-auth-ink mb-2">
                        {t('accountPage.credentials.labels.password')}
                      </Typography>
                      <Input
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        // type="password"
                        placeholder="•••••••••••••••"
                        variant="bordered"
                        className="w-full h-[2.375rem]"
                        classNames={{
                          inputWrapper: `border-auth-strength-none max-h-[2.375rem] min-h-[2.375rem] rounded-lg h-10 ${
                            errors.password && touched.password ? 'border-red-500' : ''
                          }`,
                        }}
                      />
                      {errors.password && touched.password && (
                        <div className="text-red-500 text-xs mt-1">{errors.password}</div>
                      )}
                    </div>
                    <Typography variant="body3" className="text-auth-muted text-xs">
                      {(() => {
                        try {
                          // Проверяем, является ли строка валидной датой
                          // Если сервер отдает локальное время с 'Z', нужно добавить offset
                          const utcDate = new Date(accountData.lastUpdated);
                          if (isNaN(utcDate.getTime())) {
                            return accountData.lastUpdated; // Возвращаем исходную строку если не дата
                          }
                          const date = new Date(
                            utcDate.getTime() + utcDate.getTimezoneOffset() * 60000
                          );
                          return date.toLocaleString('ru-RU');
                        } catch {
                          return accountData.lastUpdated;
                        }
                      })()}
                    </Typography>
                  </div>
                </CardBody>
              </Card>

              <CustomHeroButton
                // type="submit"
                // color="primary"
                onPress={handleSubmit}
                className="mt-6"
                disabled={isUpdating || !isValid}
                isLoading={isUpdating}
                title={
                  isUpdating
                    ? t('accountPage.credentials.buttons.updating')
                    : t('accountPage.credentials.buttons.provide')
                }
              />
            </Form>
          )}
        </Formik>

        {/* Error Alert Block */}
        {error && (
          <AlertBlock
            data={[
              {
                severity: 'error',
                title: t('accountPage.credentials.error.title'),
                description: error,
              },
            ]}
            type="rounded"
            className="mt-4"
          />
        )}
      </div>
      <AlertBlock
        data={[
          {
            severity: 'info',
            title: t('accountPage.credentials.info.security.title'),
            description: t('accountPage.credentials.info.security.description'),
          },
          {
            severity: 'info',
            title: t('accountPage.credentials.info.alternatives.title'),
            icon: <img src={IcUsr} />,
            descriptions: [
              t('accountPage.credentials.info.alternatives.description1'),
              t('accountPage.credentials.info.alternatives.description2'),
            ],
          },
        ]}
        className="mb-4"
      />

      {/* Success Modal */}
      <CredentialsSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
      />
    </div>
  );
};

export default CredentialsTab;
